import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  collection,
  query,
  orderBy,
  where,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  writeBatch,
  deleteField,
  serverTimestamp,
  Timestamp,
  documentId
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useAutomation } from './AutomationContext';
import { useCompany } from './CompanyContext';
import { sanitizeForFirestore } from '../utils/firestoreUtils';

const LeadsContext = createContext();

export const useLeads = () => {
  const context = useContext(LeadsContext);
  if (!context) {
    throw new Error('useLeads must be used within a LeadsProvider');
  }
  return context;
};

// Helper to revive Firestore Timestamps
const reviveTimestamps = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(item => reviveTimestamps(item));
  }

  // Handle Timestamp-like objects
  if (obj.seconds !== undefined && obj.nanoseconds !== undefined) {
    return new Timestamp(obj.seconds, obj.nanoseconds);
  }

  // Recursively process object properties
  const revived = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      revived[key] = reviveTimestamps(obj[key]);
    }
  }
  return revived;
};

export const LeadsProvider = ({ children }) => {
  const { companyId } = useCompany();
  const { runAutomationsForEvent } = useAutomation();
  const [leads, setLeads] = useState([]);
  const [isLeadsLoading, setIsLeadsLoading] = useState(true);

  // ── Bounded leads window ────────────────────────────────────────────────────
  // Default: leads created since Jan 1 of LAST year (covers Pipeline's
  // current-year default + one archive year). Selecting older Pipeline years,
  // "All years", or searching calls ensureLeadsSince(dateOrNull) to expand —
  // null loads the full collection intentionally.
  const [leadsSince, setLeadsSince] = useState(() => new Date(new Date().getFullYear() - 1, 0, 1));

  const ensureLeadsSince = useCallback((date) => {
    setLeadsSince((current) => {
      if (current === null) return current;             // already unbounded
      if (date === null) return null;                   // intentional full load
      const d = date instanceof Date ? date : new Date(date);
      if (Number.isNaN(d.getTime())) return current;
      return d < current ? d : current;
    });
  }, []);

  // ── Targeted lead backfill ─────────────────────────────────────────────────
  // Operational surfaces (Dispatch/Calendar/Today) may reference leads created
  // before the window (old customers with current jobs). ensureLeadIds() loads
  // those specific docs and merges them in, keeping lookups complete.
  const [extraLeadIds, setExtraLeadIds] = useState([]);
  const [extraLeads, setExtraLeads] = useState(() => new Map());

  const ensureLeadIds = useCallback((ids) => {
    setExtraLeadIds((prev) => {
      const missing = (ids || []).filter((id) => id && !prev.includes(id));
      if (missing.length === 0) return prev;
      return [...prev, ...missing].slice(-300);
    });
  }, []);

  useEffect(() => {
    if (!db || !companyId || extraLeadIds.length === 0) return;
    const leadsRef = collection(db, 'companies', companyId, 'leads');
    const chunks = [];
    for (let i = 0; i < extraLeadIds.length; i += 10) {
      chunks.push(extraLeadIds.slice(i, i + 10));
    }
    const unsubs = chunks.map((chunk) =>
      onSnapshot(
        query(leadsRef, where(documentId(), 'in', chunk)),
        (snapshot) => {
          setExtraLeads((prev) => {
            const next = new Map(prev);
            snapshot.docs.forEach((docSnap) => {
              next.set(docSnap.id, { id: docSnap.id, ...reviveTimestamps(structuredClone(docSnap.data())) });
            });
            return next;
          });
        },
        (error) => console.error('Leads backfill snapshot error:', error),
      )
    );
    return () => unsubs.forEach((u) => u());
  }, [companyId, extraLeadIds]);

  // Listen to leads collection (windowed by createdAt)
  useEffect(() => {
    if (!db || !companyId) {
      // Not ready yet; AppReadyGate will prevent this provider from mounting.
      return;
    }

    const leadsRef = collection(db, 'companies', companyId, 'leads');
    const q = leadsSince
      ? query(leadsRef, where('createdAt', '>=', Timestamp.fromDate(leadsSince)), orderBy('createdAt', 'desc'))
      : query(leadsRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const leadsData = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          // Use structuredClone to deep copy, then revive timestamps
          const clonedData = structuredClone(data);
          const revivedData = reviveTimestamps(clonedData);

          // Migration: Convert old "program" field to "service"
          if (revivedData.program && !revivedData.service) {
            revivedData.service = revivedData.program;
          }

          return {
            id: docSnap.id,
            ...revivedData
          };
        });
        setLeads(leadsData);
        setIsLeadsLoading(false);
      },
      (error) => {
        console.error('Leads snapshot error:', error);
        setIsLeadsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [companyId, leadsSince]);

  // Add new lead
  const addLead = async (payload) => {
    if (!companyId) {
      throw new Error('Missing companyId');
    }

    try {
      const leadsRef = collection(db, 'companies', companyId, 'leads');
      const currentUser = auth.currentUser;
      const newLead = sanitizeForFirestore({
        ...payload,
        createdBy: currentUser ? currentUser.uid : null,
        createdAt: serverTimestamp(),
        lastContact: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      const docRef = await addDoc(leadsRef, newLead);

      // Run automations for leadCreated
      try {
        await runAutomationsForEvent('leadCreated', {
          lead: { id: docRef.id, ...newLead },
        });
      } catch (err) {
        console.error('Error running leadCreated automations:', err);
      }

      return docRef.id;
    } catch (error) {
      console.error('Error adding lead:', error);
      throw error;
    }
  };

  // Update existing lead
  const updateLead = async (id, payload) => {
    if (!companyId) {
      throw new Error('Missing companyId');
    }
    try {
      const leadRef = doc(db, 'companies', companyId, 'leads', id);

      // Detect stage change before update
      let oldStage = null;
      const existing = leads.find((l) => l.id === id);
      if (existing) {
        oldStage = existing.stage || null;
      }

      await updateDoc(
        leadRef,
        sanitizeForFirestore({
          ...payload,
          updatedAt: serverTimestamp(),
        })
      );

      // When a lead moves TO 'Approved' from any other stage, unschedule any linked
      // job tasks so they re-appear in the Scheduler's unscheduled rail.
      if (payload.stage === 'Approved' && oldStage && oldStage !== 'Approved') {
        try {
          const tasksRef = collection(db, 'companies', companyId, 'tasks');
          const tasksQ = query(tasksRef, where('leadId', '==', id));
          const tasksSnap = await getDocs(tasksQ);

          const batch = writeBatch(db);
          let hasUpdates = false;

          tasksSnap.forEach((taskDoc) => {
            const taskData = taskDoc.data();
            const isJob = taskData.type === 'job' || taskData.taskCategory === 'job';
            // Only clear scheduling fields on tasks that are actually scheduled
            if (isJob && taskData.scheduledStart) {
              // Explicit null (NOT deleteField) — the bounded TasksContext
              // "unscheduled" listener matches scheduledStart == null.
              batch.update(taskDoc.ref, {
                scheduledStart: null,
                scheduledEnd: null,
                status: 'approved',
                updatedAt: serverTimestamp(),
              });
              hasUpdates = true;
            }
          });

          if (hasUpdates) {
            await batch.commit();
          }
        } catch (err) {
          // Non-fatal: log but don't block the stage transition
          console.error('[LeadsContext] Failed to unschedule job tasks on Approved transition:', err);
        }
      }

      // If stage changed, run automations
      if (payload.stage && payload.stage !== oldStage) {
        try {
          await runAutomationsForEvent('stageChanged', {
            leadId: id,
            lead: existing,   // full lead object — required for sendSMS phone interpolation
            from: oldStage,
            to: payload.stage,
          });
        } catch (err) {
          console.error('Error running stageChanged automations:', err);
        }
      }
    } catch (error) {
      console.error('Error updating lead:', error);
      throw error;
    }
  };

  // Sprint 292 — Lifecycle remediation.
  // The bare `deleteDoc(leads/{id})` primitive is intentionally gone. Any
  // caller that reaches for `deleteLead` will hit this stub and be told to
  // use Close Account (normal flow) or the owner-only backend Purge
  // endpoint (rare). Retained as a stub so any straggler imports don't
  // silently crash — they get a clear error.
  const deleteLead = async () => {
    const msg =
      '[LeadsContext.deleteLead] Raw hard delete is disabled. ' +
      'Use Close Account (POST /api/leads/{id}/account/close) for normal ' +
      'end-of-service, or the owner-only Purge workflow ' +
      '(POST /api/leads/{id}/purge) for permanent removal.';
    console.error(msg);
    throw new Error(msg);
  };

  // Merge windowed leads with targeted backfill (windowed wins on conflicts)
  const mergedLeads = React.useMemo(() => {
    if (extraLeads.size === 0) return leads;
    const map = new Map(extraLeads);
    leads.forEach((l) => map.set(l.id, l));
    return [...map.values()];
  }, [leads, extraLeads]);

  const value = {
    leads: mergedLeads,
    isLeadsLoading,
    leadsSince,
    ensureLeadsSince,
    ensureLeadIds,
    addLead,
    updateLead,
    deleteLead
  };

  return <LeadsContext.Provider value={value}>{children}</LeadsContext.Provider>;
};

export default LeadsContext;
